import { requiresNewValidationKey } from './requiresNewValidationKey';

export const ADDRESS_VALIDATION_SUBMIT_STRATEGIES = Object.freeze({
  // Prefill pattern, and the user chose not to save the address to their
  // profile. The address only goes into the in-progress form.
  FORM_ONLY: 'FORM_ONLY',
  // The address needs a fresh override validation key fetched before saving.
  UPDATE_VALIDATION_KEY: 'UPDATE_VALIDATION_KEY',
  // The address can be saved directly.
  CREATE_TRANSACTION: 'CREATE_TRANSACTION',
});

export const USER_ENTERED_ADDRESS_ID = 'userEntered';

const buildAnalyticsEvent = ({
  userHasBadAddress,
  analyticsSectionName,
  suggestedAddressSelected,
}) => {
  const addressSuggestionUsed = suggestedAddressSelected ? 'yes' : 'no';

  if (userHasBadAddress) {
    return {
      event: 'api_call',
      'api-name': 'Updating bad address',
      'api-status': 'started',
      'profile-section': analyticsSectionName,
      'profile-addressSuggestionUsed': addressSuggestionUsed,
    };
  }

  return {
    event: 'profile-transaction',
    'profile-section': analyticsSectionName,
    'profile-addressSuggestionUsed': addressSuggestionUsed,
  };
};

/**
 * Everything the class's onSubmit decided, as a pure function. Returns a plan
 * rather than performing it, so the dispatching and the analytics call stay in
 * the hook.
 *
 * `analyticsEvent` is null for the form-only strategy: the class returned
 * before reaching recordEvent on that path.
 *
 * @returns {{
 *   type: string,
 *   payload: object,
 *   method: 'PUT'|'POST',
 *   shouldClearValidationKey: boolean,
 *   analyticsEvent: object|null,
 * }}
 */
export const getAddressValidationSubmitStrategy = ({
  selectedAddress,
  selectedAddressId,
  overrideValidationKey,
  analyticsSectionName,
  prefillPatternEnabled = false,
  updateProfileChoice,
  userHasBadAddress = false,
}) => {
  const payload = { ...selectedAddress, overrideValidationKey };

  if (prefillPatternEnabled && updateProfileChoice === 'no') {
    return {
      type: ADDRESS_VALIDATION_SUBMIT_STRATEGIES.FORM_ONLY,
      payload,
      method: null,
      shouldClearValidationKey: false,
      analyticsEvent: null,
      updateProfileChoice,
    };
  }

  const suggestedAddressSelected =
    selectedAddressId !== USER_ENTERED_ADDRESS_ID;

  // Derived before the key is stripped below; it only ever depends on whether
  // the address already exists in the profile.
  const method = payload.id ? 'PUT' : 'POST';

  if (suggestedAddressSelected) {
    // A suggested address is already confirmed, and sending an override key
    // alongside it makes the API error.
    delete payload.overrideValidationKey;
  }

  return {
    type: requiresNewValidationKey(payload)
      ? ADDRESS_VALIDATION_SUBMIT_STRATEGIES.UPDATE_VALIDATION_KEY
      : ADDRESS_VALIDATION_SUBMIT_STRATEGIES.CREATE_TRANSACTION,
    payload,
    method,
    shouldClearValidationKey: suggestedAddressSelected,
    analyticsEvent: buildAnalyticsEvent({
      userHasBadAddress,
      analyticsSectionName,
      suggestedAddressSelected,
    }),
  };
};
